import { useEffect, useRef } from 'react';
import { Toast } from 'common-package';
import { BarcodeScanner, BarcodeFormat, BarcodeScannedEvent } from '@capacitor-mlkit/barcode-scanning';
import { throttle } from 'lodash-es';
import { Dialog } from 'antd-mobile';
import { AndroidSettings, NativeSettings } from 'capacitor-native-settings';
import { useMemoizedFn } from 'ahooks';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import scanBorder from '@/assets/image/scan-border.png';
import { isNewSoftwareVersion } from '@/utils';
import { ossPrefix } from '@/constant';

type Point = [number, number];

// 检查点在不在
const isPointInPolygon = (point: Point, polygon: Point[]): boolean => {
  let x = point[0],
    y = point[1];
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    let xi = polygon[i][0],
      yi = polygon[i][1];
    let xj = polygon[j][0],
      yj = polygon[j][1];
    let intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

// 隐藏整个页面
export const hiddenWholePage = () => {
  const rootElement = document.getElementById('root');
  const drawerElement = document.querySelector('.ant-drawer') as HTMLElement;
  // 隐藏Toast
  const toastElement = document.querySelector('.adm-mask-content') as HTMLElement;

  if (rootElement) {
    rootElement.style.display = 'none';
  }

  if (drawerElement) {
    drawerElement.style.visibility = 'hidden';
  }
  if (toastElement) {
    toastElement.style.visibility = 'hidden';
  }
};

// 显示整个页面
export const showWholePage = () => {
  const rootElement = document.getElementById('root');
  const drawerElement = document.querySelector('.ant-drawer') as HTMLElement;
  const toastElement = document.querySelector('.adm-mask-content') as HTMLElement;

  if (rootElement) {
    rootElement.style.display = 'initial';
  }
  if (drawerElement) {
    drawerElement.style.visibility = 'visible';
  }
  if (toastElement) {
    toastElement.style.visibility = 'visible';
  }
};

const addScanBorderDom = () => {
  const container = document.createElement('div');

  container.style.position = 'absolute';
  container.style.top = '50%';
  container.style.left = '50%';
  container.style.transform = 'translate(-50%, -50%)';
  container.id = 'scan-border-container';
  container.style.zIndex = '1000';

  Array.from({ length: 4 }).forEach((image, index) => {
    const img = document.createElement('img');
    img.src = scanBorder;

    img.style.transform = `rotate(${90 * index}deg)`; // Rotate the image 90 degrees
    img.style.position = 'absolute';
    img.style.top = '-268px';
    img.style.left = '-268px';
    container?.appendChild(img);
  });

  document.body?.appendChild(container);
};

const removeScanBorderDom = () => {
  const container = document.getElementById('scan-border-container');
  container && document.body.removeChild(container);
};

/**
 * 扫描识别二维码
 * @returns
 */
export const useScanQRCode = ({ tips = '扫描耗材二维码' }: { tips: string }) => {
  const ignoreScanResult = useRef(false);
  const currentScanCodeList = useRef<string[]>([]);

  useEffect(() => {
    return () => {
      Device.getInfo().then(result => {
        if (result?.platform === 'android') {
          App.getInfo().then(info => {
            if (isNewSoftwareVersion(info.version, '1.8.0')) {
              BarcodeScanner.stopScan();
            }
          });
        }
      });
    };
  }, []);

  const handleDeniedPermission = () => {
    Dialog.alert({
      content: '系统检测到摄像头权限被关闭，请开启理疗师工作台的摄像头权限。',
      confirmText: '去开启',
      closeOnMaskClick: true,
      onConfirm() {
        NativeSettings.openAndroid({
          option: AndroidSettings.ApplicationDetails,
        })
          .then(status => {
            console.log('打开系统设置状态：', status);
          })
          .catch(e => {
            console.error('NativeSettings.openAndroid Error：', e);
          });
      },
    });
  };

  const scanQR = useMemoizedFn((scanCodeList?: Array<'consumableUniqueCode' | 'verifyCode'>, tipsText?: string) => {
    return new Promise<string[]>(async (resolve, reject) => {
      ignoreScanResult.current = false;
      currentScanCodeList.current = [];
      const data = await Device.getInfo();

      if (data.platform === 'web') {
        reject({ message: '仅可在PAD上使用扫码功能' });
        return;
      }

      /** 执行扫码 */
      const handleStartScan = async () => {
        addCloseIcon(reject);

        await handleDOMOnStartScan(tipsText);

        // 扫码结果在监听中获取
        BarcodeScanner.addListener(
          'barcodeScanned',
          throttle(async (result: BarcodeScannedEvent) => {
            if (ignoreScanResult.current) return;

            const limitScanArea: Point[] = [
              [703, 209],
              [1440, 218],
              [1459, 953],
              [709, 968],
            ];
            const { rawValue, cornerPoints } = result.barcode;
            // 判断四个点是否都在规定的范围内
            for (const item of cornerPoints) {
              if (!isPointInPolygon(item, limitScanArea)) {
                return;
              }
            }

            // 支持同时扫多个码
            if (scanCodeList?.length > 1) {
              ignoreScanResult.current = true;

              // 判断耗材码和服务卡码是否符合规则
              if (QR_CODE_VALID_RULES[currentScanCodeList.current?.length]?.(rawValue)) {
                currentScanCodeList.current.push(rawValue);
              } else {
                Toast.show('该二维码不符合要求，请重新扫描');
                ignoreScanResult.current = false;
                return;
              }

              if (currentScanCodeList.current?.length === 1) {
                Dialog.confirm({
                  title: '耗材码识别成功',
                  content: '耗材码识别成功，请继续扫描识别服务卡',
                  confirmText: '继续扫码',
                  onConfirm: async () => {
                    const qrTipDiv = document.getElementById('qr-tip');
                    !!qrTipDiv && (qrTipDiv.innerHTML = '扫描服务卡二维码');
                    ignoreScanResult.current = false;
                  },
                  onCancel: async () => {
                    await BarcodeScanner.removeAllListeners();
                    BarcodeScanner.stopScan();
                    handleDOMOnStopScan();
                    reject('取消扫码');
                  },
                });
              }

              if (currentScanCodeList.current?.length >= scanCodeList?.length) {
                await BarcodeScanner.removeAllListeners();
                BarcodeScanner.stopScan();
                resolve(currentScanCodeList.current);
                handleDOMOnStopScan();
              }
            } else {
              await BarcodeScanner.removeAllListeners();

              const verifyRule =
                scanCodeList?.[0] === 'consumableUniqueCode' ? QR_CODE_VALID_RULES[0] : QR_CODE_VALID_RULES[1];

              // 修复开启扫码后瞬间识别到值时，UI无法恢复的问题
              setTimeout(() => {
                BarcodeScanner.stopScan();
                handleDOMOnStopScan();

                if (verifyRule?.(rawValue)) {
                  resolve([rawValue]);
                } else {
                  reject({ message: '该二维码不符合要求，请重新扫描' });
                }
              }, 100);
            }
          }, 1000)
        );

        BarcodeScanner.addListener('scanError', async err => {
          BarcodeScanner.stopScan();
          BarcodeScanner.removeAllListeners();
          reject(err);
        });

        // 开启摄像头进行二维码扫描
        await BarcodeScanner.startScan({
          formats: [BarcodeFormat.QrCode],
        });
      };

      const status = await BarcodeScanner.checkPermissions();

      if (status?.camera === 'denied') {
        handleDeniedPermission();
        reject('未获取到摄像头权限');
      } else if (status?.camera === 'granted') {
        handleStartScan();
      } else {
        // 请求授权
        Toast.show('当前需要进行核销，我们需要访问您的摄像头以进行扫码操作。请授予摄像头权限。');
        const status = await BarcodeScanner.requestPermissions();
        if (status?.camera === 'granted') {
          handleStartScan();
        } else {
          handleDeniedPermission();
          reject('未获取到摄像头权限');
        }
      }
    });
  });

  // 底部提示框
  const addTipBox = (text: string) => {
    const toastBox = document.createElement('div');
    toastBox.innerHTML = text;
    toastBox.style.position = 'fixed';
    toastBox.style.bottom = '54px';
    toastBox.style.left = '50%';
    toastBox.style.transform = 'translateX(-50%)';
    toastBox.style.backgroundColor = '#00000033';
    toastBox.style.color = '#fff';
    toastBox.style.padding = '8px 25px';
    toastBox.style.borderRadius = '65px';
    toastBox.style.fontSize = '16px';
    toastBox.style.display = 'block';
    toastBox.style.zIndex = '1000';
    toastBox.id = 'qr-tip';
    document.body?.appendChild(toastBox);
  };

  // 移除底部提示框
  const removeTipBox = () => {
    const ele = document.getElementById('qr-tip');
    ele && document.body.removeChild(document.getElementById('qr-tip'));
  };

  /**+
   * 开启摄像头时的DOM处理
   */
  const handleDOMOnStartScan = useMemoizedFn((tipsText?: string) => {
    return new Promise(resolve => {
      setTimeout(() => {
        hiddenWholePage();
        addTipBox(tipsText || tips);
        addScanBorderDom();
        resolve('');
      }, 500);
    });
  });

  /**
   * 关闭摄像头时的DOM处理
   */
  const handleDOMOnStopScan = useMemoizedFn(() => {
    removeTipBox();
    removeCloseIcon();
    removeScanBorderDom();
    showWholePage();
  });

  // 左上角关闭按钮
  const addCloseIcon = useMemoizedFn((reject: (value: string | PromiseLike<string>) => void) => {
    const closeIcon = document.createElement('img');
    closeIcon.src = ossPrefix + '/index_module/index/lupg5jlt_703040.png';
    closeIcon.style.position = 'fixed';
    closeIcon.style.top = '31px';
    closeIcon.style.left = '42px';
    closeIcon.style.width = '23px';
    closeIcon.style.height = '23px';

    closeIcon.style.zIndex = '1000';
    closeIcon.id = 'qr-cancel-control';

    closeIcon.addEventListener('click', async () => {
      handleDOMOnStopScan();
      await BarcodeScanner.removeAllListeners();
      await BarcodeScanner.stopScan();
      reject?.('取消扫码');
    });
    document.body?.appendChild(closeIcon);
  });

  const removeCloseIcon = useMemoizedFn(() => {
    const elem = document.getElementById('qr-cancel-control');
    elem && document.body.removeChild(elem);
  });

  return { scanQR };
};

// 二维码内容有效规则集
const QR_CODE_VALID_RULES: Array<(value: string) => boolean> = [
  // 耗材码规则
  (value: string) => value?.length <= 32,
  // 服务卡规则
  (value: string) => value?.includes('app?type=serverCard&code'),
];

const isValidQRCodeContent = (content: string) => {
  if (!content?.length) return false;
  return QR_CODE_VALID_RULES.some(rule => rule(content));
};
